import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from './rn';
import AppSheet from './AppSheet';
import { fontSize, radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { createBackup, restoreBackup } from '../e2ee/backup';
import { InfoButton, InfoText, useInfo } from './InfoHint';

type Props = {
  visible: boolean;
  token: string;
  onClose: () => void;
  /** Testte gerçek ağ/kripto yerine sahtesi geçilebilsin diye. */
  actions?: {
    create: (token: string) => Promise<{ code: string; messages: number }>;
    restore: (token: string, code: string) => Promise<number | null>;
  };
};

/**
 * Sohbet yedeği ve kurtarma kodu (9.34 · D-600).
 *
 * Bu ekranın taşıdığı asıl bilgi teknik değil, DÜRÜSTLÜK: kodu kaybeden kişi
 * geçmişini kaybeder ve biz onu geri veremeyiz. Uyarıyı küçük bir dipnot yapmak,
 * kullanıcının anlamadığı bir riski üstlenmesi olurdu; o yüzden kodun hemen
 * altında ve kapatmadan önce görülmek zorunda.
 */
export default function ChatBackupSheet({ visible, token, onClose, actions }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const explainInfo = useInfo();
  const styles = React.useMemo(() => makeStyles(colors), [colors]);

  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [restoreCode, setRestoreCode] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const create = actions?.create ?? createBackup;
  const restore = actions?.restore ?? restoreBackup;

  const kapat = useCallback(() => {
    setCode(null);
    setRestoreCode('');
    setNotice(null);
    onClose();
  }, [onClose]);

  const yedekAl = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      const { code: yeni } = await create(token);
      setCode(yeni);
    } catch {
      setNotice(t('backup.failed'));
    } finally {
      setBusy(false);
    }
  }, [create, token, t]);

  const geriYukle = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      const adet = await restore(token, restoreCode);
      // `null` hem yanlış kod hem eksik yedek demek: kullanıcı için ikisi de
      // "geçmiş açılamadı". Hangisi olduğunu söylemek, kodu deneyen birine
      // yedek var mı bilgisini vermek olurdu.
      setNotice(adet === null ? t('backup.restoreFailed') : t('backup.restored', { count: adet }));
    } catch {
      setNotice(t('backup.restoreFailed'));
    } finally {
      setBusy(false);
    }
  }, [restore, token, restoreCode, t]);

  return (
    // 7.14 / D-704 — Apple'ın sayfa kuralı: Kapat üstte solda (ortak AppSheet).
    // D-654 — nasıl çalıştığı ⓘ arkasında; kurtarma kodu uyarısı görünür kalır.
    <AppSheet
      visible={visible}
      title={t('backup.title')}
      onClose={kapat}
      testID="backup"
      titleAccessory={<InfoButton open={explainInfo.open} onPress={explainInfo.toggle} subject={t('backup.title')} testID="backup-info" />}>
            <InfoText open={explainInfo.open} text={t('backup.explainInfo')} style={styles.explain} testID="backup-info-text" />

            {code ? (
              <View testID="backup-code-block" style={styles.codeBlock}>
                <Text style={styles.codeLabel}>{t('backup.codeTitle')}</Text>
                <Text testID="backup-code" style={styles.code} selectable>{code}</Text>
                <Text style={styles.warning}>{t('backup.codeWarning')}</Text>
              </View>
            ) : null}

            <TouchableOpacity
              testID="backup-create"
              style={[styles.primary, busy && styles.disabled]}
              disabled={busy}
              onPress={yedekAl}
            >
              <Text style={styles.primaryText}>
                {code ? t('backup.refresh') : t('backup.create')}
              </Text>
            </TouchableOpacity>

            <View style={styles.divider} />

            <Text style={styles.sectionTitle}>{t('backup.restoreTitle')}</Text>
            <TextInput
              testID="backup-restore-input"
              style={styles.input}
              value={restoreCode}
              onChangeText={setRestoreCode}
              placeholder={t('backup.restorePlaceholder')}
              placeholderTextColor={colors.gray400}
              autoCapitalize="characters"
              autoCorrect={false}
            />
            <TouchableOpacity
              testID="backup-restore"
              style={[styles.primary, (busy || !restoreCode.trim()) && styles.disabled]}
              disabled={busy || !restoreCode.trim()}
              onPress={geriYukle}
            >
              <Text style={styles.primaryText}>{t('backup.restore')}</Text>
            </TouchableOpacity>

            {notice ? <Text testID="backup-notice" style={styles.notice}>{notice}</Text> : null}

    </AppSheet>
  );
}

// Temada uyarı rengi yok; kırmızı hem açık hem koyu zeminde okunur olmalı.
const WARNING_RED = '#E53935';

function makeStyles(colors: ReturnType<typeof useTheme>) {
  return StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
    sheet: {
      backgroundColor: colors.white,
      borderTopLeftRadius: radius.layerOuter,
      borderTopRightRadius: radius.layerOuter,
      maxHeight: '90%',
    },
    content: { padding: spacing.lg, gap: spacing.md },
    title: { fontSize: fontSize.lg, fontWeight: '700', color: colors.black, flexShrink: 1 },
    titleRow: { flexDirection: 'row', alignItems: 'center' },
    explain: { fontSize: fontSize.sm, color: colors.gray400, lineHeight: 20 },
    sectionTitle: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
    codeBlock: {
      backgroundColor: colors.gray100,
      borderRadius: radius.card,
      padding: spacing.md,
      gap: spacing.sm,
    },
    codeLabel: { fontSize: fontSize.sm, color: colors.gray400 },
    code: {
      fontSize: fontSize.md,
      fontWeight: '700',
      color: colors.black,
      letterSpacing: 1,
      fontVariant: ['tabular-nums'],
    },
    warning: { fontSize: fontSize.sm, color: WARNING_RED, lineHeight: 20 },
    primary: {
      backgroundColor: colors.black,
      borderRadius: radius.control,
      paddingVertical: spacing.md,
      alignItems: 'center',
    },
    primaryText: { color: colors.white, fontWeight: '600', fontSize: fontSize.md },
    disabled: { opacity: 0.5 },
    divider: { height: 1, backgroundColor: colors.gray200, marginVertical: spacing.sm },
    input: {
      borderWidth: 1,
      borderColor: colors.gray200,
      borderRadius: radius.control,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      color: colors.black,
      fontSize: fontSize.md,
    },
    notice: { fontSize: fontSize.sm, color: colors.black },
    close: { alignItems: 'center', paddingVertical: spacing.md },
    closeText: { color: colors.gray400, fontWeight: '600' },
  });
}
